import { capitalize, toISO8601LocalString } from 'utils/helpers';
import { requestPartyDocuments } from 'graphql/documents/mutations';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { DocumentsType } from 'v2-types/order';

import { AssociationType } from '../../types';
import { orderPartiesAlphabetically } from '../../utils/orderDocumentsAlphabetically';
import { PartyFile, RequestPartyFiles } from './types';

type UseRequestDocumentsParams = {
	parties: AssociationType[];
	documents: DocumentsType[];
	onClose: () => void;
};

export const useRequestDocuments = ({ parties, documents, onClose }: UseRequestDocumentsParams) => {
  const { t } = useTranslation();
  const Alert = useAlert();
  const { id: orderId } = useParams<{ id: string }>();
  const [errors, setErrors] = useState<null | string>(null);
  const {
    handleSubmit,
    control,
    getValues,
    reset: resetForm,
    formState: { isValid: isFormValid },
    setValue,
  } = useForm<RequestPartyFiles>({
    defaultValues: {
      expiresIn: '',
      notBefore: '',
      partyId: '',
      files: [],
    },
    mode: 'onChange',
  });

  const filteredParties = useMemo(() => orderPartiesAlphabetically(
    parties.filter((party) =>
      documents.some(
        ({ data, kind }) =>
          kind === 'upload' &&
            data.some((fileData) => fileData._id === party._id && fileData.kind === 'party')
      )
    )
  ).map((party) => {
    const associationName = capitalize(
      party.name.length > 40 ? party.name.slice(0, 40).concat('...') : party.name
    );
    const kindList = party.kinds && party.kinds.length > 0 ? `(${party.kinds?.join(', ')})` : '';
    return {
      ...party,
      name: associationName + kindList,
    };
  }), [parties, documents]);

  const onSelectPartyId = useCallback(
    (partyId: string) => {
      setValue('partyId', partyId);
      const files: PartyFile[] = documents
        .filter(
          (file) =>
            file.kind === 'upload' &&
						file.data.some((fileParty) => fileParty._id === partyId && fileParty.kind === 'party')
        )
        .map((file) => ({
          fileId: file._id,
          filename: file.description,
          checked: false,
        }));
      setValue('files', files, { shouldDirty: true, shouldValidate: true });
    },
    [documents, setValue]
  );

  const { mutate: requestDocuments, isLoading: isRequestDocumentsLoading } = useMutation(
    requestPartyDocuments,
    {
      onSuccess: () => {
        setErrors(null);
        Alert(t('documents:document-request-sended'), 'success');
        onClose();
      },
      onError: (e: { response: { errors: { message: string }[] } }) => {
        const errorMessage =
					e.response && e.response.errors && e.response.errors.length > 0
					  ? e.response.errors[0].message
					  : t('documents:lack-of-email');
        setErrors(errorMessage);
        Alert(errorMessage, 'error');
      },
    }
  );

  const onSendRequest = handleSubmit(async (requestInfo) => {
    const notBeforeDate = new Date(requestInfo.notBefore);
    notBeforeDate.setHours(0, 0, 0, 0);

    const expiresInDate = new Date(requestInfo.expiresIn);
    expiresInDate.setHours(23, 59, 59, 0);

    requestDocuments({
      id: orderId,
      notBefore: toISO8601LocalString(notBeforeDate, false)!,
      expiresIn: toISO8601LocalString(expiresInDate, false)!,
      requests: requestInfo.files
        .filter(({ checked }) => checked)
        .map(({ fileId }) => ({
          file_id: fileId,
          party_id: requestInfo.partyId,
        })),
    });
  });

  return {
    filteredParties,
    onSelectPartyId,
    t,
    isRequestDocumentsLoading,
    isFormValid,
    control,
    getValues,
    onSendRequest,
    errors,
    setErrors,
  };
};
